import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import type { Tone, WithClassName } from './types';

export type Step = {
  id: string;
  label: string;
  description?: ReactNode;
  state: 'complete' | 'current' | 'upcoming' | 'error' | 'skipped';
};
export type StepperProps = WithClassName & {
  label: string;
  steps: readonly Step[];
  onRevisit?: (id: string) => void;
};

const states: Record<Step['state'], { label: string; tone: Tone; icon?: string }> = {
  complete: { label: 'Complete', tone: 'success', icon: '✓' },
  current: { label: 'Current', tone: 'info' },
  upcoming: { label: 'Upcoming', tone: 'neutral' },
  error: { label: 'Error', tone: 'danger', icon: '!' },
  skipped: { label: 'Skipped', tone: 'neutral', icon: '−' },
};

export const Stepper = forwardRef<HTMLOListElement, StepperProps>(function Stepper({
  label, steps, onRevisit, className,
}, ref) {
  return <ol ref={ref} role="list" aria-label={label} className={`ui-stepper ${className ?? ''}`}>
    {steps.map((step, index) => <li key={step.id}
      className={`ui-stepper__step ui-tone-${states[step.state].tone}`}
      aria-current={step.state === 'current' ? 'step' : undefined}>
      <span className="ui-stepper__marker" aria-hidden="true">{states[step.state].icon ?? index + 1}</span>
      <div className="ui-stepper__content">
        {step.state === 'complete' && onRevisit
          ? <button type="button" className="ui-button" onClick={() => onRevisit(step.id)}>{step.label}</button>
          : <span className="ui-stepper__label">{step.label}</span>}
        <div className="ui-stepper__state">{states[step.state].label}</div>
        {step.description !== undefined && <div>{step.description}</div>}
      </div>
    </li>)}
  </ol>;
});
